/**
 * iOS-feel haptics. Unified wrapper that routes to real native haptics inside
 * a Capacitor shell (iOS/Android binary) and falls back to the Web Vibration
 * API everywhere else. Public API is unchanged from v1 — all existing call
 * sites (BottomNav, long-press, scanner, pull-to-refresh, etc.) keep working.
 *
 * Behavior matrix:
 *   - Capacitor native (iOS/Android app) → @capacitor/haptics (real Taptic Engine)
 *   - Android browser / Android PWA      → navigator.vibrate (buzzes)
 *   - iOS Safari / iOS PWA               → silent (browser limitation)
 *   - Desktop                            → silent
 *   - prefers-reduced-motion             → silent on every path
 *
 * Use the named presets instead of calling `navigator.vibrate` directly:
 *   - tap()      → light selection/tap (nav, chips, toggles)
 *   - impact()   → medium confirm (primary action, FAB)
 *   - longPress()→ long-press detected
 *   - success()  → success notification
 *   - warning()  → warning notification
 *   - error()    → error notification
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

type Pattern = number | number[];

function reducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function canVibrate(): boolean {
  if (typeof navigator === 'undefined') return false;
  return 'vibrate' in navigator;
}

function webFire(pattern: Pattern) {
  if (!canVibrate()) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* ignore */
  }
}

function nativeImpact(style: ImpactStyle) {
  // Fire-and-forget; native plugin returns a Promise we don't need to await.
  Haptics.impact({ style }).catch(() => {
    /* ignore */
  });
}

function nativeNotification(type: NotificationType) {
  Haptics.notification({ type }).catch(() => {
    /* ignore */
  });
}

export const haptics = {
  /** Light tap — selection changes, nav tabs, chips, toggles. */
  tap: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeImpact(ImpactStyle.Light);
    webFire(8);
  },
  /** Medium impact — primary CTA, FAB, confirm. */
  impact: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeImpact(ImpactStyle.Medium);
    webFire(14);
  },
  /** Long-press detected. */
  longPress: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeImpact(ImpactStyle.Heavy);
    webFire(10);
  },
  /** Success notification. */
  success: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeNotification(NotificationType.Success);
    webFire([10, 40, 18]);
  },
  /** Warning notification. */
  warning: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeNotification(NotificationType.Warning);
    webFire([14, 60, 14]);
  },
  /** Error notification. */
  error: () => {
    if (reducedMotion()) return;
    if (isNative()) return nativeNotification(NotificationType.Error);
    webFire([20, 50, 20, 50, 20]);
  },
};
